import { useState } from 'react';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  getBetaListForwardingAddressesQuery,
  getUserQuery,
} from '../../../../../api/users-api';
import DeleteForwardingAddressesDialog from './delete-forwarding-address-dialog';
import AddForwardingAddressDialog from './add-forwarding-address-dialog';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../../utils/use-user-settings';
import { useParams } from 'react-router-dom';
import { Messages } from '../../../../../utils/message-constants';
import { createGridOverlays } from '../../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../../shared/shared-toolbar-latest';
import CreateButton from '../../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../../shared/buttons/shared-icon-button';

import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Mail from '@mui/icons-material/Mail';

export default function ForwardAddressGrid() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles, primaryDomain } = useUserSettings();
  const { data: user } = useQuery(getUserQuery(userEmail));
  const queryClient = useQueryClient();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openDeleteForwardingAddresses, setOpenDeleteForwardingAddresses] =
    useState(false);
  const [openAddForwardingAddress, setOpenAddForwardingAddress] =
    useState(false);

  const {
    data: forwardingEmails,
    isLoading,
    isFetching: gmailLabelsFetching,
  } = useQuery(getBetaListForwardingAddressesQuery(userEmail));

  const columns: GridColDef[] = [
    {
      field: 'forwardingEmail',
      headerName: 'Forwarding Address',
      editable: false,
      sortable: false,
      filterable: false,
      display: 'flex',
      flex: 1,
    },
    {
      field: 'verificationStatus',
      headerName: 'Status',
      editable: false,
      sortable: false,
      filterable: false,
      display: 'flex',
      flex: 1,
    },
  ];

  if (!user) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        primaryButtons={
          <CreateButton
            onClick={() => setOpenAddForwardingAddress(true)}
            disabled={
              !isAbleToSeeUIElement(
                userRoles,
                UserRoles.AddGmailForwardAddress.value
              )
            }
            label="Add Address"
            tooltipLabel={
              !isAbleToSeeUIElement(
                userRoles,
                UserRoles.AddGmailForwardAddress.value
              )
                ? Messages.Errors.Permission.Acting(
                    UserRoles.AddGmailForwardAddress.name
                  )
                : 'Add Address'
            }
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="delete"
              onClick={() => setOpenDeleteForwardingAddresses(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.DeleteGmailForwardAddress.value
                )
              }
              tooltipLabel={
                !isAbleToSeeUIElement(
                  userRoles,
                  UserRoles.DeleteGmailForwardAddress.value
                )
                  ? Messages.Errors.Permission.Acting(
                      UserRoles.DeleteGmailForwardAddress.name
                    )
                  : 'Delete'
              }
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.ForwardingAddresses, userEmail],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      />
    );
  }

  const tableData = forwardingEmails ? forwardingEmails : [];

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Forwarding Addresses', <Mail />),
        }}
        getRowId={(row) => row.forwardingEmail}
        rows={tableData}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        toolbarQuickFilterWidth={400}
        loading={isLoading || gmailLabelsFetching}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        disableColumnSelector
        checkboxSelection
        disableColumnMenu
      />
      <AddForwardingAddressDialog
        open={openAddForwardingAddress}
        handleClose={() => setOpenAddForwardingAddress(false)}
        userEmail={userEmail}
        forwardingEmails={tableData}
        primaryDomain={primaryDomain}
        changeLogRoles={changeLogRoles}
      />
      {selectedRows.length > 0 ? (
        <DeleteForwardingAddressesDialog
          open={openDeleteForwardingAddresses}
          handleClose={() => setOpenDeleteForwardingAddresses(false)}
          selectedRows={selectedRows}
          userEmail={userEmail}
          forwardingEmails={tableData}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
